import { useState } from "react";
import styled from "styled-components";
import FlexComp from "components/reusable/flex/Flex";
import { Input } from "components/reusable/input/Inputs";
import SecDropdownComp from "components/reusable/dropdownComp/SecDropdown";
import { Button } from "components/reusable/button/Button";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { apiReq } from "services/Requests";
import { API_BASE_URL, API_ROUTES } from "utils/apiRoutes.constant";
import { FormHelperText, Stack } from "@mui/material";
import { createUserWithEmailAndPassword, updateProfile, signOut, getAuth } from "firebase/auth";
import { deleteApp } from "firebase/app";
import { storage, db, createSecondaryApp } from "services/firebaseSetup";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, setDoc } from "firebase/firestore";
import { SyncLoader } from "react-spinners";
import { toast } from "react-toastify";

const UserForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.user; // Get the user data from the router state
  const isEditMode = !!user; // Check if we're in edit mode

  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(user?.role || null);

  // Create validation schema based on whether we're editing or creating
  const getValidationSchema = () => {
    const baseSchema = {
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      address: Yup.string().required("Address is required"),
      phone: Yup.string().required("Phone number is required"),
      role: Yup.string().required("Role is required"),
    };

    // Add password validation based on whether we're editing or creating
    if (isEditMode) {
      // For editing, password is optional
      baseSchema.password = Yup.string()
        .min(6, "Password must be at least 6 characters");
    } else {
      // For new users, password is required
      baseSchema.password = Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required");
    }

    return Yup.object().shape(baseSchema);
  };

  // Helper function to get a user-friendly error message
  const getErrorMessage = (error) => {
    // Firebase auth error codes: https://firebase.google.com/docs/auth/admin/errors
    switch (error.code) {
      case 'auth/email-already-in-use':
        return 'This email is already registered. Please use a different email.';
      case 'auth/invalid-email':
        return 'The email address is not valid.';
      case 'auth/weak-password':
        return 'The password is too weak. Please use a stronger password.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      default:
        return error.message || 'An unexpected error occurred. Please try again.';
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      email: user?.email || "",
      address: user?.address || "",
      phone: user?.phone || "",
      role: user?.role || "",
      password: "", // keep password empty initially
      fireBaseUid: user?.fireBaseUid || "",
    },
    validationSchema: getValidationSchema(),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setLoading(true);
        if (user) {
          // Update user
          const formData = new FormData();
          formData.append("userId", user._id);
          formData.append("firstName", values.firstName);
          formData.append("lastName", values.lastName);
          formData.append("email", values.email);
          formData.append("address", values.address);
          formData.append("phone", values.phone);
          formData.append("role", values.role);
          formData.append("password", values.password);
          await apiReq(
            API_ROUTES.userManagementUpdate,
            "POST",
            formData,
            API_BASE_URL,
            false
          );
          toast.success("User updated successfully");
          navigate("/user-management");
        } else {
          // Create new user logic here...
          try {
            const fullName = `${values.firstName}_${values.lastName}`;
            
            // Create a secondary Firebase app instance for user creation
            // This prevents the newly created user from affecting the current admin's session
            const secondaryApp = createSecondaryApp();
            
            const secondaryAuth = getAuth(secondaryApp);
            
            // Create the new user using the secondary auth instance
            const res = await createUserWithEmailAndPassword(
              secondaryAuth,
              values.email,
              values.password
            );
            
            const storageRef = ref(storage, fullName);
            const uploadTask = uploadBytesResumable(storageRef);
            
            uploadTask.on(
              "state_changed",
              null,
              (error) => {
                toast.error(`Error uploading profile: ${error.message}`);
              },
              () => {
                getDownloadURL(uploadTask.snapshot.ref).then(
                  async (downloadURL) => {
                    await updateProfile(res.user, {
                      displayName: fullName,
                    });
                  }
                );
              }
            );
            
            // Use the main db instance for consistency
            await setDoc(doc(db, "users", res.user.uid), {
              uid: res.user.uid,
              displayName: fullName,
              email: values.email,
              role: values.role,
            });
            
            await setDoc(doc(db, "usersChats", res.user.uid), {});
            
            values.fireBaseUid = res.user.uid;
            await apiReq(API_ROUTES.registerAdminSide, "POST", values);
            
            // Sign out the newly created user from the secondary auth
            await signOut(secondaryAuth);
            
            // Delete the secondary app to clean up
            await deleteApp(secondaryApp);
            
            toast.success("User created successfully");
            navigate("/user-management");
          } catch (firebaseError) {
            const errorMessage = getErrorMessage(firebaseError);
            toast.error(errorMessage);
            throw firebaseError; // Re-throw to prevent navigation
          }
        }
      } catch (error) {
        console.error("Error:", error);
        // Don't show another toast if it was already shown in the inner try/catch
        if (!error.code) {
          toast.error(error.message || "An unexpected error occurred. Please try again.");
        }
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const options = [
    { value: "Staff", label: "Staff" },
    { value: "Admin", label: "Admin" },
  ];

  return (
    <>
      <RentalPropertyContainer>
        <h2 style={{ paddingLeft: "5px" }}>
          {isEditMode ? "Edit User" : "Add New User"}
        </h2>
        <RentalPropertyWrapper>
          <FlexComp direction="row" gridGap="25px" width="100%">
            <Stack direction="column" sx={{ width: "49%" }}>
              <Input
                label="First Name"
                id="outlined-size-small"
                size="size"
                placeholder="Enter"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
              />
              <FormHelperText error>{formik.errors?.firstName}</FormHelperText>
            </Stack>
            <Stack direction="column" sx={{ width: "49%" }}>
              <Input
                label="Last Name"
                id="outlined-size-small"
                defaultValue=""
                size="size"
                height={50}
                placeholder="Enter"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
              />
              <FormHelperText error>{formik.errors?.lastName}</FormHelperText>
            </Stack>
            <Stack direction="column" sx={{ width: "49%" }}>
              <Input
                label="Email"
                id="outlined-size-small"
                defaultValue=""
                size="size"
                height={50}
                placeholder="Enter"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
              />
              <FormHelperText error>{formik.errors?.email}</FormHelperText>
            </Stack>
          </FlexComp>
          <FlexComp direction="row" gridGap="25px" width="100%">
            <Stack direction="column" sx={{ width: "32%" }}>
              <Input
                label="Address"
                id="outlined-size-small"
                defaultValue=""
                size="size"
                placeholder="Enter"
                name="address"
                value={formik.values.address}
                onChange={formik.handleChange}
              />
              <FormHelperText error>{formik.errors?.address}</FormHelperText>
            </Stack>
            <Stack direction="column" sx={{ width: "32%" }}>
              <Input
                label="Contact"
                id="outlined-size-small"
                defaultValue=""
                size="size"
                height={50}
                placeholder="+1 | Enter"
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
              />
              <FormHelperText error>{formik.errors?.phone}</FormHelperText>
            </Stack>
            <Stack direction="column" sx={{ width: "32%" }}>
              <SecDropdownComp
                label="Role"
                id="location-select"
                placeholder="select"
                defaultValue="Role"
                focused
                size="small"
                name="role"
                options={options}
                value={formik.values.role}
                selectedMonth={selectedOption}
                handleChange={(event) =>
                  formik.setFieldValue("role", event.target.value)
                }
              />
              <FormHelperText error>{formik.errors.role}</FormHelperText>
            </Stack>
          </FlexComp>
          <FlexComp direction="row" gridGap="25px" width="100%">
            <Stack direction="column" sx={{ width: "32%" }}>
              <Input
                label="Password"
                id="outlined-size-small"
                defaultValue=""
                size="size"
                placeholder={isEditMode ? "Default to Password" : "Enter Password"}
                name="password"
                type="password"
                value={formik.values.password}
                onChange={formik.handleChange}
              />
              <FormHelperText error>{formik.errors?.password}</FormHelperText>
            </Stack>
          </FlexComp>
          <FlexComp direction="row" gridGap="25px" width="100%">
            <Button
              text={loading ? <SyncLoader color={"#FFFFFF"} size={8} /> : "Save"}
              backgroundColor="rgba(11, 37, 88, 1)"
              color="white"
              width="112px"
              type="submit"
              onClick={formik.submitForm}
              isLoading={loading}
            />
            <Button
              text="Cancel"
              type="button"
              onClick={() => navigate("/user-management")}
              backgroundColor="rgba(239, 243, 253, 1)"
              color="rgba(174, 182, 207, 1)"
              width="112px"
              isLoading={loading}
            />
          </FlexComp>
        </RentalPropertyWrapper>
      </RentalPropertyContainer>
    </>
  );
};

export default UserForm;

const RentalPropertyContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 20px;
  background: #fff;
  width: 100%;
  padding: 20px;
  min-height: 85vh;
`;

const RentalPropertyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 50px;
`;
